// ─────────────────────────────────────────────
//  ReferenceCard.jsx — Referencia: precio + porción
// ─────────────────────────────────────────────

import React, { useRef, useEffect } from 'react';
import appStyles from '../layout/CalculatorPage.module.css';
import styles from './ReferenceCard.module.css';

import { formatCurrency } from '../../utils/format';

const MAX_PRICE = 1000000;

function sanitize(val) {
	let v = val.replace(/[^0-9.]/g, '');
	const parts = v.split('.');
	if (parts.length > 2) v = parts[0] + '.' + parts.slice(1).join('');
	if (parts[1] !== undefined && parts[1].length > 2) {
		v = parts[0] + '.' + parts[1].slice(0, 2);
	}
	return v;
}

export default function ReferenceCard({
	options,
	refGrams,
	refPrice,
	onRefGramsChange,
	onRefPriceChange,
	pricePerGram,
	hasReference,
	onReset,
}) {
	const inputRef = useRef(null);

	useEffect(() => {
		const focusInput = () => {
			inputRef.current?.focus({ preventScroll: true });
		};
		focusInput();
		requestAnimationFrame(focusInput);
		const timer = window.setTimeout(focusInput, 100);
		return () => window.clearTimeout(timer);
	}, []);

	const handleReset = () => {
		onReset?.();
		inputRef.current?.focus();
	};

	const handleChange = (e) => {
		const sanitized = sanitize(e.target.value);
		if (parseFloat(sanitized) > MAX_PRICE) return;
		onRefPriceChange(sanitized);
	};

	return (
		<>
			{/* ── Top Bar ── */}
			<div className={`${appStyles.topBar} ${styles.topBar20}`}>
				<div className={styles.topRow}>
					<p className={styles.unitLabel}>Introduce precio y porción de referencia</p>
				</div>
			</div>

			<section className={styles.card} aria-label="Precio de referencia">

			{refPrice ? (
				<button
					type="button"
					className={`${styles.resetBtn} ${styles.label}`}
					onClick={handleReset}
					aria-label="Reiniciar calculadora"
				>
					<span>↺</span> Reiniciar
				</button>
			) : ''}

			<div
				className={styles.inputWrap}
				onClick={() => inputRef.current?.focus()}
			>
				<span className={styles.currency} aria-hidden="true">$</span>
				<input
					ref={inputRef}
					className={styles.input}
					type="text"
					inputMode="decimal"
					autoFocus
					value={refPrice}
					onChange={handleChange}
					placeholder="Precio de referencia"
					aria-label="Precio de la referencia en pesos"
				/>
			</div>

			<div
				className={styles.quickBtns}
				role="group"
				aria-label="Peso de referencia"
			>
				{options.map((opt) => (
					<button
						key={opt.id}
						type="button"
						className={`${styles.quickBtn} ${refGrams === opt.grams ? styles.quickBtnActive : ''}`}
						onClick={() => onRefGramsChange(opt.grams)}
						aria-pressed={refGrams === opt.grams}
						aria-label={`Porción de ${opt.label}`}
					>
						{opt.label}
					</button>
				))}
			</div>

			{hasReference && (
				<p className={styles.rateLine} aria-live="polite">
					≈ {formatCurrency(pricePerGram * 1000)} por kg
					<span className={styles.rateDot} aria-hidden="true"> · </span>
					{formatCurrency(pricePerGram)} por g
				</p>
			)}
			</section>
		</>
	);
}
